import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { Pagination } from 'app/_models/pagination.model';
import { AssetTypesService } from 'app/_services/asset-types.service';
import { ContractorService } from 'app/_services/contractor.service';
import { NotificationService } from 'app/_services/notification.service';
import { SiteService } from 'app/_services/site.service';
import { SiteFormsService } from 'app/_services/site-forms.service';
import { SiteWorkOrderProceduresService } from 'app/_services/site-work-order-procedures.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'procedure-form-page',
  templateUrl: './procedure-form-page.component.html',
})
export class ProcedureFormPageComponent implements OnInit {
  public selectedSiteId: any;
  public selectedContractorId: any;
  public siteOwnerId: any;
  public returnUrl: string;
  public createOrEdit: string = 'CREATE';
  public submitLabel: string = 'Create Procedure';
  public isSiteContext: boolean = true;
  public procedureToEdit: any;
  public tabitems: any[] = [];

  public allowCompulsoryCheckOnSiteForm: boolean;
  public allowCompulsoryCheckOffSiteForm: boolean;

  public saveInProgress = false;
  public siteConfigLoading = false;
  public assetCategoriesLoading = false;
  public siteFormsLoading = false;

  public selectedSite: any;
  public selectedContractor: any;
  public form: FormGroup;
  public assetCategories: any[] = [];
  public selectedDocuments: any[] = [];
  public siteFormsOptions: any[] = [];

  constructor(
    private procedureService: SiteWorkOrderProceduresService,
    private assetCatService: AssetTypesService,
    private notificationService: NotificationService,
    private siteFormsService: SiteFormsService,
    private siteService: SiteService,
    private contractorService: ContractorService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private globals: Globals,
    private fb: FormBuilder,
  ) { }

  ngOnInit(): void {
    this.isSiteContext = this.router.url.includes('/work-order-procedures/');
    this.createOrEdit = this.activatedRoute.snapshot.params.procedureId ? 'EDIT' : 'CREATE';
    this.submitLabel = this.createOrEdit === 'EDIT' ? 'Save Procedure' : 'Create Procedure';

    this.selectedSiteId = this.isSiteContext ? this.activatedRoute.snapshot.params.id : undefined;
    this.selectedContractorId = this.isSiteContext
      ? undefined
      : (this.activatedRoute.snapshot.params.id || this.globals.contractor.maintenanceId);
    this.siteOwnerId = Number(this.activatedRoute.snapshot.queryParams.siteOwnerId);
    this.returnUrl = this.activatedRoute.snapshot.queryParamMap.get('returnUrl');

    const allowCheckOn = this.activatedRoute.snapshot.queryParams.allowCompulsoryCheckOnSiteForm;
    const allowCheckOff = this.activatedRoute.snapshot.queryParams.allowCompulsoryCheckOffSiteForm;

    this.allowCompulsoryCheckOnSiteForm = allowCheckOn === true || allowCheckOn === 'true';
    this.allowCompulsoryCheckOffSiteForm = allowCheckOff === true || allowCheckOff === 'true';

    this.form = this.fb.group({
      name: new FormControl('', [Validators.required, Validators.maxLength(30)]),
      description: new FormControl('', [Validators.required]),
      revision: new FormControl('1.0', [Validators.required]),
      minPartySize: new FormControl(1, [Validators.required, Validators.min(1), Validators.max(30)]),
      assetCategory: new FormControl('', [Validators.required]),
      siteFormCheckOn: new FormControl(),
      siteFormCheckOff: new FormControl(),
      preapproved: new FormControl(false),
    });

    this.getAllAssetTypes();

    if (this.isSiteContext) {
      this.getAllSiteForms();
      this.loadPageSiteData();
    } else {
      this.allowCompulsoryCheckOnSiteForm = false;
      this.allowCompulsoryCheckOffSiteForm = false;
      this.loadPageContractorData();
    }

    if (this.createOrEdit === 'EDIT') {
      this.loadProcedureToEdit();
    }
  }

  public get pageLoading(): boolean {
    return this.siteConfigLoading || this.assetCategoriesLoading || this.siteFormsLoading;
  }

  private loadPageSiteData() {
    this.siteConfigLoading = true;
    this.siteService.getSiteById(this.selectedSiteId).subscribe(
      (data: any) => {
        this.selectedSite = data;
        if (!this.siteOwnerId) {
          this.siteOwnerId = data.siteOwner.id;
        }
        if (this.allowCompulsoryCheckOnSiteForm === undefined) {
          this.allowCompulsoryCheckOnSiteForm = data.siteOwner.siteOwnerUser.allowCompulsoryCheckOnSiteForm;
        }
        if (this.allowCompulsoryCheckOffSiteForm === undefined) {
          this.allowCompulsoryCheckOffSiteForm = data.siteOwner.siteOwnerUser.allowCompulsoryCheckOffSiteForm;
        }
      },
      (error) => {
        this.siteConfigLoading = false;
        throw error;
      },
      () => {
        this.siteConfigLoading = false;
      }
    );
  }

  private loadPageContractorData() {
    this.contractorService.getContractorById(this.selectedContractorId).subscribe((data: any) => {
      this.selectedContractor = data;
      const routeRole = this.globals.user.routeRole;

      if (routeRole === 'contractor-manager') {
        this.tabitems = [
          { path: '/' + routeRole + '/company/details', title: 'Details' },
          { path: '/' + routeRole + '/company/contacts', title: 'Contacts' },
          { path: '/' + routeRole + '/company/certificates', title: 'Certificates' },
          { path: '/' + routeRole + '/company/procedures', title: 'Procedures' },
        ];
      } else {
        this.tabitems = [
          { path: '/' + routeRole + '/contractors/', title: 'All Contractors', class: 'back-link' },
          { path: '/' + routeRole + '/contractors/' + this.selectedContractorId + '/details', title: 'Details' },
          { path: '/' + routeRole + '/contractors/' + this.selectedContractorId + '/workers', title: 'Workers' },
          { path: '/' + routeRole + '/contractors/' + this.selectedContractorId + '/certificates', title: 'Certificates' },
          { path: '/' + routeRole + '/contractors/' + this.selectedContractorId + '/contacts', title: 'Contacts' },
          { path: '/' + routeRole + '/contractors/' + this.selectedContractorId + '/procedures', title: 'Procedures' },
        ];
      }
    });
  }

  private getAllAssetTypes() {
    this.assetCategoriesLoading = true;
    const callback = (data: any) => {
      data.forEach((element) => {
        this.assetCategories.push({
          label: element.name,
          value: element,
        });
      });
      if (this.createOrEdit === 'EDIT') {
        this.patchFormForEdit();
      }
    };

    const errorCallback = (error) => {
      this.assetCategoriesLoading = false;
      throw error;
    };

    const completeCallback = () => {
      this.assetCategoriesLoading = false;
    };

    if (this.isSiteContext) {
      this.assetCatService.getAllAssetTypesForSite(this.selectedSiteId).subscribe(callback, errorCallback, completeCallback);
      return;
    }

    this.assetCatService.getAllAssetTypesNoPagination().subscribe(callback, errorCallback, completeCallback);
  }

  private getAllSiteForms() {
    this.siteFormsLoading = true;
    const pagination: Pagination = {
      size: 10000,
      page: 0,
      totalRecords: 0
    };

    this.siteFormsService.getAllBySite(this.selectedSiteId, pagination, ['siteForm.name']).subscribe((siteForms: any) => {
      this.siteFormsOptions = [{ label: 'Select site form...', value: null }];
      siteForms.body.forEach((element) => {
        const item = {
          label: element.name,
          value: element.id,
        };
        this.siteFormsOptions.push(item);
      });

      if (this.createOrEdit === 'EDIT') {
        this.patchFormForEdit();
      }
    }, error => {
      this.siteFormsLoading = false;
      throw error;
    }, () => {
      this.siteFormsLoading = false;
    });
  }

  public submit() {
    if (this.saveInProgress || this.form.invalid) {
      return;
    }

    const values = this.form.value;
    if (this.createOrEdit === 'EDIT') {
      this.onEditProcedureFormSubmit();
      return;
    }

    this.saveInProgress = true;

    const body = {
      name: values.name,
      description: values.description,
      revision: values.revision,
      minimumPartySize: values.minPartySize,
      assetCategory: values.assetCategory.value.id,
      checkOnSiteFormId: values.siteFormCheckOn,
      checkOffSiteFormId: values.siteFormCheckOff,
      siteId: this.selectedSiteId,
      maintenanceId: this.selectedContractorId,
      preapproved: !!values.preapproved,
      manufacturer: '',
      models: [],
      ratings: [],
      documents: this.selectedDocuments,
    };

    this.procedureService.createWorkOrderProcedure(body).subscribe((data: any) => {
      this.saveInProgress = false;
      Swal.fire(
        'Success!',
        'The new procedure has been created. You can now add competencies directly on the procedure details page and edit them as needed.',
        'success'
      );
      this.navigateAfterCreate(data);
    }, error => {
      this.saveInProgress = false;
      throw error;
    });
  }

  private onEditProcedureFormSubmit() {
    this.saveInProgress = true;
    const values = this.form.value;

    const body = {
      id: this.procedureToEdit.id,
      name: values.name,
      description: values.description,
      revision: values.revision,
      minimumPartySize: values.minPartySize,
      assetCategoryId: values.assetCategory.value.id,
      checkOnSiteFormId: values.siteFormCheckOn,
      checkOffSiteFormId: values.siteFormCheckOff,
      siteId: this.selectedSiteId,
      maintenanceId: this.selectedContractorId,
      preapproved: !!values.preapproved,
      // This form does not edit competencies. They still need to be sent back on update
      // because the backend treats the procedure payload as a full replacement.
      competencies: this.getCompetenciesForUpdate(),
      manufacturer: this.procedureToEdit.manufacturer || '',
      models: this.normalizeProcedureArrayValues(this.procedureToEdit.models),
      ratings: this.normalizeProcedureArrayValues(this.procedureToEdit.ratings),
    };

    this.procedureService.updateWorkOrderProcedure(body).subscribe(() => {
      this.saveInProgress = false;
      this.notificationService.notify('success', 'Updated!', 'The  procedure description has been updated.');
      this.navigateToProcedureDetails(this.procedureToEdit.id);
    }, error => {
      this.saveInProgress = false;
      throw error;
    });
  }

  private loadProcedureToEdit() {
    const routeProcedureId = Number(this.activatedRoute.snapshot.params.procedureId);
    if (!routeProcedureId) {
      Swal.fire('Error', 'Procedure id missing from edit route.', 'error');
      this.navigateBackToList();
      return;
    }

    this.procedureService.getWorkOrderProcedureById(routeProcedureId).subscribe((data: any) => {
      this.procedureToEdit = data;
      this.patchFormForEdit();
    }, () => {
      this.procedureToEdit = null;
      Swal.fire('Error', 'Unable to load selected procedure. Please select it again from list.', 'error');
      this.navigateBackToList();
    });
  }

  private patchFormForEdit() {
    if (!this.procedureToEdit || !this.assetCategories.length) {
      return;
    }

    const selectedAssetCategory = this.assetCategories.find(
      category => category.value.id === this.procedureToEdit.assetCategory.id
    );

    this.form.patchValue({
      name: this.procedureToEdit.name,
      description: this.procedureToEdit.description,
      revision: this.procedureToEdit.revision,
      minPartySize: this.procedureToEdit.minimumPartySize,
      preapproved: this.procedureToEdit.preapproved,
      siteFormCheckOn: this.procedureToEdit.checkOnSiteFormId,
      siteFormCheckOff: this.procedureToEdit.checkOffSiteFormId,
      assetCategory: selectedAssetCategory || {
        label: this.procedureToEdit.assetCategory.name,
        value: this.procedureToEdit.assetCategory,
      },
    });
  }

  private normalizeProcedureArrayValues(values: any): string[] {
    return Array.isArray(values)
      ? values
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
        .map((value) => String(value))
      : [];
  }

  private getCompetenciesForUpdate(): any[] {
    return (this.procedureToEdit.competencies || []).map((competency) => ({
      competencyTypeId: Number(competency.competencyTypeId),
      applyToParty: !!competency.applyToParty,
      manufacturer: competency.manufacturer || '',
      models: this.normalizeProcedureArrayValues(competency.models),
      ratings: this.normalizeProcedureArrayValues(competency.ratings),
    }));
  }

  public onFileChange(event) {
    for (let i = 0; i < event.target.files.length; i++) {
      const file = event.target.files[i];
      this.selectedDocuments.push(file);
    }

    event.target.value = '';
  }

  public openSelectedDocument = (file: File) => {
    const fileUrl = URL.createObjectURL(file);
    const viewer = window.open(fileUrl, '_blank');

    if (viewer) {
      viewer.addEventListener('load', () => URL.revokeObjectURL(fileUrl), { once: true });
      setTimeout(() => URL.revokeObjectURL(fileUrl), 10000);
      return;
    }

    URL.revokeObjectURL(fileUrl);
    this.notificationService.notify('danger', 'Error', 'Could not open the selected file.');
  }

  public removeSelectedDocument = (index: number) => {
    this.selectedDocuments.splice(index, 1);
  }

  public cancel() {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }

    this.navigateBackToList();
  }

  public navigateBackToList() {
    const routeRole = this.globals.user.routeRole;

    if (this.isSiteContext) {
      this.router.navigate(['/' + routeRole + '/sites/' + this.selectedSiteId + '/work-order-procedures']);
      return;
    }

    if (routeRole === 'contractor-manager') {
      this.router.navigate(['/' + routeRole + '/company/procedures']);
      return;
    }

    this.router.navigate(['/' + routeRole + '/contractors/' + this.selectedContractorId + '/procedures']);
  }

  private navigateAfterCreate(createdProcedure: any) {
    const procedureId = createdProcedure && createdProcedure.id;

    if (this.isSiteContext && procedureId) {
      const routeRole = this.globals.user.routeRole;
      this.router.navigate(['/' + routeRole + '/sites/' + this.selectedSiteId + '/work-order-procedures/view-procedure/' + procedureId]);
      return;
    }

    this.navigateBackToList();
  }

  private navigateToProcedureDetails(procedureId: number) {
    if (this.isSiteContext && procedureId) {
      const routeRole = this.globals.user.routeRole;
      this.router.navigate(['/' + routeRole + '/sites/' + this.selectedSiteId + '/work-order-procedures/view-procedure/' + procedureId]);
      return;
    }

    this.navigateBackToList();
  }
}
